import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import cs from 'classnames';
import moment from 'moment-tz';
import {browserHistory, WithRouterProps} from 'react-router';

import {
  useAttempt,
  useAttemptQuiz,
  useQuiz,
  useQuizVersionDetail,
} from 'app/quizzes/api';
import {AttemptQuizContent} from 'app/quizzes/components/AttemptQuizPage/AttemptQuizContent';
import {TakeQuizPage} from 'app/quizzes/components/TakeQuizPage/TakeQuizPage';
import {gt} from 'app/utils/i18n';

type Props = WithRouterProps<{
  hashid: string;
  versionid: null | string;
  attemptid?: string;
}>;

export function AttemptResultPage({params}: Props) {
  const {attemptid: attemptId, versionid: versionId, hashid} = params;
  const {data: attempt} = useAttempt(attemptId != null ? attemptId : skipToken);
  const {data: quiz} = useQuiz(hashid != null ? hashid : skipToken);
  const {data: quizVersion} = useQuizVersionDetail(
    attempt ? {versionId: attempt.quiz_version.id} : skipToken
  );

  const [attemptQuiz] = useAttemptQuiz();

  if (attempt == null) return null;

  const onRetry = () => {
    attemptQuiz({quiz: attempt.quiz.url})
      .unwrap()
      .then((attempt) => {
        browserHistory.push(attempt.take_url);
      });
  };

  const attemptDuration = attempt.duration || 0;
  const quizDuration = quiz?.duration || 0;

  const attemptPercentage = attempt.percentage || 0;
  const quizPassMark = quiz?.pass_mark || 0;

  return (
    <TakeQuizPage
      versionId={versionId}
      hashid={hashid}
      attemptId={attemptId}
      showAttempts={true}
    >
      <>
        {attempt.status == 'completed' && (
          <div className="mb-4">
            <div>
              <strong>{gt('Score: ')}</strong>
              {attempt.score} out of {attempt.total}
            </div>
            <div>
              <strong>{gt('Percentage: ')}</strong>
              <span
                className={cs({
                  'text-danger': attemptPercentage < quizPassMark,
                  'text-success': attemptPercentage > quizPassMark,
                })}
              >
                {attempt.percentage}%
              </span>
            </div>
            {attempt.duration ? (
              <div>
                <strong>{gt('Duration: ')}</strong>
                <span
                  className={cs({
                    'text-danger': attemptDuration > quizDuration,
                  })}
                >
                  {moment.duration(attempt.duration, 'seconds').humanize()}
                </span>
              </div>
            ) : null}
          </div>
        )}
        {attempt.status === 'pending' && (
          <div className="mb-4 alert alert-warning" role="alert">
            {gt('This attempt is currently incomplete.')}
          </div>
        )}
        <AttemptQuizContent
          attempt={attempt}
          content={quizVersion?.content}
          onRetry={onRetry}
        />
      </>
    </TakeQuizPage>
  );
}
